import { type FormEvent, useEffect, useMemo, useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { Loader, Pagination } from '@lms/ui';
import { PERMISSIONS } from '@lms/shared-types';
import { SearchEmptyState } from '@/components/SearchEmptyState';
import { SearchInput } from '@/components/SearchInput';
import { HubBackLink } from '@/components/hub/HubBackLink';
import { useDebouncedValue } from '@/hooks/useDebouncedValue';
import { usePermissions } from '@/hooks/usePermissions';
import { api } from '@/lib/api';
import { CategoryListItem, type CategoryTreeNode } from './components/CategoryListItem';
import { CourseListCard, type CourseListItem } from './components/CourseListCard';

interface CoursesResponse {
  data: CourseListItem[];
  meta: { total: number; page: number; perPage: number; lastPage: number };
}

function findCategory(nodes: CategoryTreeNode[], id: string): CategoryTreeNode | null {
  for (const node of nodes) {
    if (node.id === id) return node;
    if (node.children?.length) {
      const found = findCategory(node.children, id);
      if (found) return found;
    }
  }
  return null;
}

export function CategoriesPage() {
  const { hasPermission } = usePermissions();
  const canCreateCategory = hasPermission(PERMISSIONS.CATEGORIAS_CREAR);
  const canCreateCourse = hasPermission(PERMISSIONS.CURSOS_CREAR);
  const [searchParams, setSearchParams] = useSearchParams();
  const selectedId = searchParams.get('category');
  const courseSearch = searchParams.get('course_search') ?? '';
  const page = Number(searchParams.get('page')) || 1;

  const [searchDraft, setSearchDraft] = useState(courseSearch);
  const debouncedCourseSearch = useDebouncedValue(searchDraft, 300).trim();

  useEffect(() => {
    setSearchParams(
      (prev) => {
        const current = prev.get('course_search') ?? '';
        if (current === debouncedCourseSearch) return prev;
        const next = new URLSearchParams(prev);
        if (debouncedCourseSearch) next.set('course_search', debouncedCourseSearch);
        else next.delete('course_search');
        next.delete('page');
        return next;
      },
      { replace: true },
    );
  }, [debouncedCourseSearch, setSearchParams]);

  const { data: tree, isLoading, error } = useQuery({
    queryKey: ['categories'],
    queryFn: () => api<CategoryTreeNode[]>('/categories'),
  });

  const selectedCategory = useMemo(
    () => (selectedId && tree ? findCategory(tree, selectedId) : null),
    [selectedId, tree],
  );

  useEffect(() => {
    if (!tree?.length) return;

    const firstId = tree[0].id;
    if (selectedCategory) return;

    setSearchParams(
      (prev) => {
        const next = new URLSearchParams(prev);
        next.set('category', firstId);
        next.delete('page');
        return next;
      },
      { replace: true },
    );
  }, [tree, selectedCategory, setSearchParams]);

  const { data: coursesData, isLoading: coursesLoading } = useQuery({
    queryKey: ['courses', 'category', selectedId, courseSearch, page],
    queryFn: () => {
      const params = new URLSearchParams();
      params.set('categoryId', selectedId!);
      params.set('page', String(page));
      params.set('perPage', '10');
      if (courseSearch) params.set('course_search', courseSearch);
      return api<CoursesResponse>(`/courses?${params.toString()}`);
    },
    enabled: Boolean(selectedId),
  });

  const courses = coursesData?.data ?? [];
  const meta = coursesData?.meta;
  const treeEmpty = !tree?.length;

  const updateParams = (updates: Record<string, string | null>) => {
    const next = new URLSearchParams(searchParams);
    for (const [key, value] of Object.entries(updates)) {
      if (value === null || value === '') next.delete(key);
      else next.set(key, value);
    }
    setSearchParams(next, { replace: true });
  };

  const selectCategory = (id: string) => {
    updateParams({ category: id, page: null, course_search: courseSearch || null });
  };

  const createCategoryUrl = selectedCategory
    ? `/categorias/create?parent_id=${selectedCategory.id}`
    : '/categorias/create';

  const createCourseUrl = selectedCategory
    ? `/courses/create?category_id=${selectedCategory.id}&return_to=categories`
    : null;

  return (
    <div className="lms-course-category-mgmt lms-site-admin-hub-page font-sans">
      <div className="mb-6 flex items-start gap-4">
        <HubBackLink to="/administracion?tab=courses" label="Volver a Administración" />
        <div className="min-w-0 flex-1">
          <h1 className="text-3xl font-semibold text-dash-primary md:text-4xl">
            Administrar categorías de cursos y cursos
          </h1>
          <p className="mt-1 max-w-2xl text-dash-on-surface-variant">
            Organiza la estructura de categorías y cursos.
          </p>
        </div>
      </div>

      {error && (
        <p className="mb-4 text-sm text-red-600" role="alert">
          {(error as Error).message}
        </p>
      )}

      {isLoading ? (
        <div className="flex justify-center py-12">
          <Loader />
        </div>
      ) : (
        <div className="ccm-split">
          <section id="category-listing" className="ccm-pane" aria-labelledby="category-listing-title">
            <div className="ccm-pane__header">
              <h2 id="category-listing-title" className="ccm-pane__title">
                Categorías
              </h2>
              {canCreateCategory && (
                <Link to={createCategoryUrl} className="ccm-btn-primary">
                  <span className="material-symbols-outlined text-[20px]" aria-hidden="true">
                    add_circle
                  </span>
                  Crear nueva categoría
                </Link>
              )}
            </div>

            {treeEmpty ? (
              <div className="ccm-empty ccm-empty--dashed">
                <span className="italic">No hay categorías registradas</span>
              </div>
            ) : (
              <ul className="ccm-category-list">
                {tree!.map((category) => (
                  <CategoryListItem
                    key={category.id}
                    category={category}
                    selectedId={selectedId}
                    onSelect={selectCategory}
                  />
                ))}
              </ul>
            )}
          </section>

          <section id="course-listing" className="ccm-pane ccm-pane--courses" aria-labelledby="course-listing-title">
            {selectedCategory ? (
              <>
                <div className="ccm-pane__header">
                  <h2 id="course-listing-title" className="ccm-pane__title">
                    {selectedCategory.name}
                  </h2>
                  {createCourseUrl && canCreateCourse && (
                    <Link to={createCourseUrl} className="ccm-btn-primary">
                      <span className="material-symbols-outlined text-[20px]" aria-hidden="true">
                        school
                      </span>
                      Crear nuevo curso
                    </Link>
                  )}
                </div>

                <form method="get" className="ccm-course-search mb-4" onSubmit={(event) => event.preventDefault()}>
                  <SearchInput
                    id="course-search"
                    name="course_search"
                    value={searchDraft}
                    onChange={setSearchDraft}
                    placeholder="Buscar cursos…"
                    aria-label="Buscar cursos"
                  />
                </form>

                {coursesLoading ? (
                  <div className="flex justify-center py-8">
                    <Loader />
                  </div>
                ) : courses.length === 0 ? (
                  courseSearch ? (
                    <SearchEmptyState term={courseSearch} onClear={() => setSearchDraft('')} />
                  ) : (
                    <div className="ccm-empty">
                      <p>No hay cursos en esta categoría.</p>
                    </div>
                  )
                ) : (
                  <>
                    <ul className="ccm-course-list">
                      {courses.map((course) => (
                        <CourseListCard
                          key={course.id}
                          course={course}
                          categoryId={selectedCategory.id}
                          courseSearch={courseSearch}
                        />
                      ))}
                    </ul>

                    <div className="ccm-course-footer">
                      <span className="ccm-course-footer__badge">
                        Mostrando {courses.length} de {meta?.total ?? courses.length} cursos
                      </span>
                    </div>

                    {meta && meta.lastPage > 1 && (
                      <div className="mt-4">
                        <Pagination
                          page={meta.page}
                          totalPages={meta.lastPage}
                          onPageChange={(nextPage) => updateParams({ page: String(nextPage) })}
                        />
                      </div>
                    )}
                  </>
                )}
              </>
            ) : (
              <>
                <div className="ccm-pane__header">
                  <h2 id="course-listing-title" className="ccm-pane__title">
                    Cursos
                  </h2>
                </div>
                <div className="ccm-empty ccm-empty--grow">
                  <p>Seleccione una categoría para ver sus cursos.</p>
                </div>
              </>
            )}
          </section>
        </div>
      )}
    </div>
  );
}
